import React from "react";
import {
  TICKET_STATUS_LABELS,
  TICKET_STATUS_CATEGORY,
} from "constants/tickets";

/**
 * TicketStatusPill (SOUND-493)
 * -------------------------------------------------------
 * Dedicated ticket status pill — deliberately NOT the shared `StatusBadge`,
 * so ticket + MID status semantics don't collide on shared keys.
 *
 * Keys on `status` (+ derived `statusCategory`) to pick a design-system badge
 * tone, then overrides two cases the category alone can't express:
 *   - `waiting`  -> warning (blocked / waiting-on-client pops out of the blue
 *                   in-progress cohort during triage)
 *   - `declined` -> destructive (a *negative* done, distinct from resolved)
 *
 * The leading dot is `bg-current` (inherits the tone) except `open`, whose dot
 * is brand — the "freshest, needs-pickup" accent.
 */

// status -> { badge tone class, dot class }
const TONE = {
  open: { badge: "badge-muted", dot: "bg-brand" },
  in_progress: { badge: "badge-info", dot: "bg-current" },
  waiting: { badge: "badge-warning", dot: "bg-current" },
  resolved: { badge: "badge-success", dot: "bg-current" },
  declined: { badge: "badge-destructive", dot: "bg-current" },
  withdrawn: { badge: "badge-muted", dot: "bg-current" },
  canceled: { badge: "badge-muted", dot: "bg-current" },
};

export default function TicketStatusPill({ status, statusCategory, className = "" }) {
  const key = status || "open";
  const category = statusCategory || TICKET_STATUS_CATEGORY[key] || "todo";
  const tone = TONE[key] || TONE.open;
  const label = TICKET_STATUS_LABELS[key] || key;

  return (
    <span
      className={`badge ${tone.badge} ${className}`}
      title={`Status: ${label}`}
      data-category={category}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} aria-hidden="true" />
      {label}
    </span>
  );
}
